import { RevampTable, TableCell } from '@design/components/table';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { memo, useMemo, useRef, useState } from 'react';

import { EarningsTableItem } from '../../Earnings';
import { EarningsTableHeaderCell } from './components/earnings-table-header/EarningsTableHeader';
import styles from './EarningsTable.module.scss';
import { SCHEDULED_DATE_COLUMN_ID } from './utils/getColumnUtils';
import { getCellHighlighted, isCellNumber } from './utils/utils';

export const EarningsTable = memo(
  ({
    tableData,
    columns,
    onRowClick,
  }: {
    tableData: EarningsTableItem[];
    columns: ColumnDef<EarningsTableItem>[];
    onRowClick: (row: EarningsTableItem) => void;
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [sortingState, setSortingState] = useState<SortingState>([
      {
        id: SCHEDULED_DATE_COLUMN_ID,
        desc: false,
      },
    ]);

    const table = useReactTable({
      data: tableData,
      columns,
      enableMultiSort: false,
      state: { sorting: sortingState },
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
    });

    const tableRows = useMemo(
      () => table.getRowModel().rows,
      [tableData, sortingState]
    );

    return (
      <div className={styles.tableScrollContainer} ref={containerRef}>
        <RevampTable data-testid="earnings-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup, index, arr) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const isLastHeaderGroup = index === arr.length - 1;
                  return (
                    <EarningsTableHeaderCell
                      key={header.id}
                      header={header}
                      isLastHeaderGroup={isLastHeaderGroup}
                      isHighlighted={getCellHighlighted(header.column.id)}
                      setActiveSortingState={setSortingState}
                    />
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {tableRows.map(row => (
              <tr key={row.id} onClick={() => onRowClick(row.original)}>
                {row.getVisibleCells().map((cell, idx) => {
                  return (
                    <TableCell
                      key={cell.id}
                      isNumber={isCellNumber(cell.column.id)}
                      className={clsx(
                        getCellHighlighted(cell.column.id) &&
                          styles.tableCellHighlighted
                      )}
                      alignment={idx > 2 ? 'end' : 'start'}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </RevampTable>
      </div>
    );
  }
);
